import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginSuccess } from 'redux/slices/authSlice';
import { setUserRole } from 'redux/slices/userRole';
import CardLoader from 'wrapper/card-loader';
import B2CLoginBadge from './B2CLoginBadge';
import { extractOrganizationContext } from 'utils/callbackRouter';
import tokenManager from 'services/auth/tokenManager';
import sessionManager from 'services/session/SessionManager';
import { landingPathForRole, mapRoleToAppRole } from 'utils/roles.js';
import { buildTenantUrl, validateOrgSlug } from 'utils/urls.js';
import { clearAuthStorage } from 'utils/storage.js';
import { validateAuthResponse, isRetryableError, getAuthErrorMessage } from 'utils/validators.js';
import {
  buildAppPath,
  getCurrentOrgSlug,
  getCurrentBranchSlug,
  getTenantSessionIndicatorKey,
  persistStateId,
  getCurrentStateId,
  buildPathForState,
} from 'utils/tenant.js';
import { rememberTenantContext } from 'utils/subdomain';
import {
  exchangeB2CSession,
  storeTenantContext,
} from 'services/b2c/consumerAuthService';
import { compositeInitAPI } from 'redux/services/client/compositeInit';

const OAuthCallback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const processingTokenRef = React.useRef(null);
  const processingKeyRef = React.useRef(null);
  const [phase, setPhase] = useState('processing');   // 'processing' | 'badge'
  const [badgeUserName, setBadgeUserName] = useState('');

  // Log on mount only (not every render)
  React.useEffect(() => {
    if (!import.meta.env.PROD) {
      console.log('[OAuthCallback] COMPONENT MOUNTED - URL:', window.location.href);
      console.log('[OAuthCallback] Search params on mount:', Object.fromEntries(searchParams));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!import.meta.env.PROD) {
      console.log('[OAuthCallback] === CALLBACK PROCESSING START ===');
      console.log('[OAuthCallback] useEffect execution #', Date.now());
      console.log('[OAuthCallback] All search params:', Object.fromEntries(searchParams));
    }

    const queryState = (searchParams.get('state_id') || searchParams.get('stateId') || '').toUpperCase() || null;

    let pathState = null;
    if (typeof window !== 'undefined') {
      const pathSegments = (window.location.pathname || '')
        .split('/')
        .filter(Boolean);
      if (pathSegments.length) {
        const candidate = pathSegments[0];
        if (/^[a-zA-Z]{2}$/.test(candidate)) {
          pathState = candidate.toUpperCase();
        }
      }
    }

    const storedState = getCurrentStateId();
    const resolvedStateId = queryState || pathState || storedState || null;
    if (queryState) {
      persistStateId(queryState);
    } else if (pathState) {
      persistStateId(pathState);
    }

    // FIRST thing - extract the token and check if we're already processing it
    const token = searchParams.get('token') || searchParams.get('session_token');
    const code = searchParams.get('code');
    const authToken = token || code;
    const stytch_token_type = searchParams.get('stytch_token_type');
    const flowParam = searchParams.get('flow');
    // Only treat as B2C if explicitly marked — stytch_token_type 'magic_links'
    // is used by BOTH B2B invites and B2C logins, so it cannot distinguish them.
    const inferredFlow = flowParam || null;
    const loginBasePath = '/login/b2b';  // the b2c login is gone

    if (!import.meta.env.PROD) {
      console.log('[OAuthCallback] Token extraction:', {
        token,
        code,
        authToken,
        stytch_token_type,
        hasSessionToken: !!searchParams.get('session_token'),
        hasCode: !!code
      });
    }

    if (!import.meta.env.PROD) {
      console.log('[OAuthCallback] Processing authentication callback');
    }

    // Check both ref AND sessionStorage to prevent duplicates across different render cycles
    const processingKey = authToken ? `processing_token_${authToken.substring(0, 20)}` : null;
    if (processingKey) {
      processingKeyRef.current = processingKey;
    }

    const existingMarker = processingKey ? sessionStorage.getItem(processingKey) : null;

    // StrictMode can invoke effects twice in development. Guard only the same in-memory run.
    if (authToken && processingTokenRef.current === authToken) {
      if (!import.meta.env.PROD) {
        console.log('[OAuthCallback] Duplicate effect invocation prevented for active token');
      }
      return;
    }

    // Recover from stale session markers after reloads/tab restores.
    if (authToken && existingMarker) {
      const existingUserForMarker = tokenManager.getUserData();
      if (existingUserForMarker) {
        const existingRole = existingUserForMarker.originalRole || existingUserForMarker.role;
        const existingRedirect =
          mapRoleToAppRole(existingRole) === 'consumer'
            ? '/dashboard'
            : landingPathForRole(existingRole);
        navigate(buildPathForState(resolvedStateId, existingRedirect), { replace: true });
        return;
      }

      if (!import.meta.env.PROD) {
        console.warn('[OAuthCallback] Clearing stale processing marker and retrying token exchange');
      }
      sessionStorage.removeItem(processingKey);
    }

    // Mark as processing IMMEDIATELY if we have a token
    if (authToken) {
      processingTokenRef.current = authToken;
      if (processingKey && !existingMarker) {
        sessionStorage.setItem(processingKey, 'processing');
      }
    }

    // Check for existing session data before clearing
    const existingToken = tokenManager.getAccessToken();
    const existingUser = tokenManager.getUserData();

    // CRITICAL FIX: Clear existing session if we have a new token (magic link or OAuth)
    // This prevents session mixing between different user sessions
    if (authToken) {
      if (existingToken || existingUser) {
        // Use scoped storage cleanup instead of clearing everything
        const preserveKeys = [processingKey].filter(Boolean);
        const preservedValues = {};
        preserveKeys.forEach(key => {
          preservedValues[key] = sessionStorage.getItem(key);
        });

        clearAuthStorage();
        tokenManager.clearToken();

        // Restore our processing flag
        Object.entries(preservedValues).forEach(([key, value]) => {
          if (value) sessionStorage.setItem(key, value);
        });
      }
    }

    // FIXED: Only skip token exchange if we have NO new token AND have valid existing session
    // When we have a new token (authToken), we ALWAYS exchange it
    const skipTokenExchange = !authToken && existingToken && existingUser;

    if (!authToken) {
      navigate(buildPathForState(resolvedStateId, `${loginBasePath}?error=no_token`));
      return;
    }

    const processCallback = async () => {
      if (!import.meta.env.PROD) {
        console.log('[OAuthCallback] Processing callback');
      }

      // Extract organization context for debugging
      const orgContext = extractOrganizationContext(searchParams);

      const stytchTokenType = stytch_token_type || 'multi_tenant_magic_links';
      const flow = inferredFlow;
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle OAuth/Stytch errors
      if (error) {
        navigate(buildPathForState(resolvedStateId, `${loginBasePath}?error=${encodeURIComponent(errorDescription || error)}`));
        return;
      }

      // ===== B2C Consumer Flow =====
      if (flow === 'b2c') {
        if (!import.meta.env.PROD) {
          console.log('[OAuthCallback] B2C consumer flow detected');
        }

        // Store tenant context from URL params
        const b2cOrgId = searchParams.get('org_id');
        const b2cBranchId = searchParams.get('branch_id');
        const b2cLoId = searchParams.get('lo_id');
        const b2cSlug = searchParams.get('slug');
        const b2cBranchSlug = searchParams.get('branch_slug');

        storeTenantContext({
          organizationId: b2cOrgId,
          branchId: b2cBranchId,
          loanOfficerId: b2cLoId,
        });
        if (b2cSlug) sessionStorage.setItem('mira_client_slug', b2cSlug);
        if (b2cBranchSlug) sessionStorage.setItem('mira_branch_slug', b2cBranchSlug);

        try {
          // Exchange session token via B2C endpoint
          const exchangeResp = await exchangeB2CSession({
            token: authToken,
            stytch_token_type: stytchTokenType,
            organization_id: b2cOrgId,
            branch_id: b2cBranchId,
          });
          const exchangeData = exchangeResp?.data ?? {};

          if (!import.meta.env.PROD) {
            console.log('[OAuthCallback] B2C exchange response:', exchangeData);
          }

          if (exchangeData.session_token) {
            tokenManager.setSessionToken(exchangeData.session_token);
          }
          if (exchangeData.session) {
            sessionManager.hydrateSessionMetadata(exchangeData.session, 'b2c_exchange');
          }

          // Notify other tabs about the new login so they reload
          sessionManager.broadcast({ type: 'SESSION_CHANGED' });

          // Signal chat to start fresh instead of restoring old session
          sessionStorage.setItem('mira_fresh_login', 'true');

          // Store user data
          const b2cUser = exchangeData.user || {};
          const mappedB2CUser = {
            ...b2cUser,
            role: 'consumer',
            originalRole: b2cUser.role || 'consumer',
          };
          tokenManager.setUserData(mappedB2CUser);

          // Auth state (loginSuccess/setUserRole) is dispatched AFTER all
          // async work completes (badge display, prefetch, etc.) together
          // with navigate(). React 18 batches them into a single render,
          // so the route swap + URL change happen atomically.

          // Show branded badge briefly
          const firstName = b2cUser.first_name || b2cUser.firstName || b2cUser.name?.split(' ')[0] || '';
          setBadgeUserName(firstName);
          setPhase('badge');

          // The exchange response already includes onboarding status — no need
          // for a separate /b2c/auth/me round-trip to check it.
          const needsOnboarding =
            b2cUser.needs_onboarding === true ||
            (b2cUser.onboarding_completed === false && b2cUser.profile_completed !== true);

          if (needsOnboarding) {
            // Brief badge display then navigate to onboard
            await new Promise(resolve => setTimeout(resolve, 800));
            if (!import.meta.env.PROD) {
              console.log('[OAuthCallback] B2C consumer needs onboarding');
            }
            sessionStorage.removeItem('mira_awaiting_magic_link');
            sessionStorage.setItem('mira_auth_verified_at', String(Date.now()));
            dispatch(loginSuccess({ user: mappedB2CUser, token: 'cookie-session', authType: 'b2c' }));
            dispatch(setUserRole('consumer'));
            navigate('/onboard', { replace: true });
            return;
          }

          // Prefetch lean composite init during login badge phase so dashboard
          // paints from warm cache on first render (single request replaces 6).
          const badgeDelay = new Promise(resolve => setTimeout(resolve, 800));
          const initQuery = dispatch(
            compositeInitAPI.endpoints.getDashboardInit.initiate(undefined, { forceRefetch: true })
          );

          await Promise.allSettled([badgeDelay, initQuery.unwrap()]);

          // Mark session as freshly verified so useAuthBootstrap skips
          // the redundant /auth/me call.
          sessionStorage.removeItem('mira_awaiting_magic_link');
          sessionStorage.setItem('mira_auth_verified_at', String(Date.now()));

          // Consumer is onboarded — dispatch auth state and navigate.
          // Client-side navigation avoids the ~14s full-page-reload penalty.
          // React 18 batches the dispatches + navigate into a single render:
          // consumer routes mount with URL already at /dashboard.
          dispatch(loginSuccess({ user: mappedB2CUser, token: 'cookie-session', authType: 'b2c' }));
          dispatch(setUserRole('consumer'));
          navigate('/dashboard', { replace: true });
          return;
        } catch (b2cErr) {
          console.error('[OAuthCallback] B2C session exchange failed:', b2cErr);
          sessionStorage.removeItem('mira_awaiting_magic_link');
          const b2cErrorMsg =
            b2cErr?.response?.data?.detail ||
            b2cErr?.message ||
            'Consumer authentication failed';
          navigate(
            `/login/consumer?error=${encodeURIComponent(b2cErrorMsg)}`
          );
          return;
        }
      }

      try {
        // Import tokenManager at try block scope for use throughout the function
        const tokenManager = (await import('services/auth/tokenManager')).default;

        // Declare variables at try block scope for use in redirect logic
        let response;
        let user;

        // For magic link tokens, we need to exchange them for a session
        let result;
        if (!skipTokenExchange && (token || code)) {
          // Magic link flow - exchange token for session
          const apiClient = (await import('services/apiClient')).default;

          if (!import.meta.env.PROD) {
            console.log('[OAuthCallback] Exchanging token for session');
          }

          // Retry logic for session exchange
          let exchangeAttempt = 0;
          const maxAttempts = 2;
          let lastError = null;

          while (exchangeAttempt < maxAttempts) {
            try {
              exchangeAttempt++;
              if (!import.meta.env.PROD) {
                console.log(`[OAuthCallback] Session exchange attempt ${exchangeAttempt}/${maxAttempts}`);
              }

              response = await apiClient.post('/auth/session/exchange', {
                token: authToken,
                stytch_token_type: stytchTokenType,
                stateId: resolvedStateId,
              });

              // Check if response indicates failure even with 200 status
              if (response.data?.success === false) {
                // Don't retry for logical errors like token already consumed
                if (response.data?.error === 'token_already_consumed') {
                  break; // Exit retry loop, will be handled in main logic
                }
                // For other logical errors, treat as failure
                throw new Error(response.data?.message || 'Authentication failed');
              }

              break; // Success, exit retry loop

            } catch (exchangeError) {
              lastError = exchangeError;
              if (!import.meta.env.PROD) {
                console.error(`[OAuthCallback] Exchange attempt ${exchangeAttempt} failed:`, exchangeError.response?.status);
              }

              // Use utility to check if it's a retryable error
              const retryable = isRetryableError(exchangeError);

              if (retryable && exchangeAttempt < maxAttempts) {
                // Wait before retrying (exponential backoff)
                const waitTime = exchangeAttempt * 1000; // 1s, 2s, etc.
                await new Promise(resolve => setTimeout(resolve, waitTime));
              } else {
                // Not retryable or max attempts reached
                throw exchangeError;
              }
            }
          }

          // If we got here without a response, throw the last error
          if (!response && lastError) {
            throw lastError;
          }

          if (!import.meta.env.PROD) {
            console.log('[OAuthCallback] Exchange response received:', {
              status: response.status,
              success: response.data?.success,
              hasUser: !!response.data?.user,
              error: response.data?.error,
              isFirstLogin: response.data?.is_first_login,
              needsOnboarding: response.data?.needs_onboarding
            });
          }

          // Use the validation utility to check the response
          const validation = validateAuthResponse(response);

          if (!validation.valid) {
            if (!import.meta.env.PROD) {
              console.error('[OAuthCallback] Response validation failed:', validation.error);
            }

            // Handle token already consumed case
            if (validation.isTokenConsumed) {
              // Check if user might already be authenticated
              const existingUser = tokenManager.getUserData();
              if (existingUser) {
                // User is already logged in, redirect to appropriate page
                const redirectPath = existingUser.role === 'superAdmin'
                  ? '/admin'
                  : '/dashboard';
                navigate(redirectPath, { replace: true });
                return;
              }

              // Token was consumed but no active session - need new link
              throw new Error('TOKEN_ALREADY_CONSUMED');
            }

            // Other validation errors from backend
            throw new Error(validation.error);
          }

          // Response is valid, process user data
          if (validation.user) {
            if (!import.meta.env.PROD) {
              console.log('[OAuthCallback] Raw validation.user from backend:', JSON.stringify(validation.user, null, 2));
              console.log('[OAuthCallback] Backend response.data structure:', JSON.stringify(response.data, null, 2));
            }
            
            // Map backend roles to frontend roles
            const backendRole = validation.user.role;
            const mappedRole = mapRoleToAppRole(backendRole);

            if (!import.meta.env.PROD) {
              console.log('[OAuthCallback] Role mapping:', {
                backendRole,
                mappedRole,
                invitation_status: validation.user.invitation_status,
                is_active: validation.user.is_active,
                needs_onboarding: validation.user.needs_onboarding,
                profile_completed: validation.user.profile_completed
              });
            }

            const sessionTokenFromResponse =
              response?.data?.session_token ||
              response?.data?.sessionToken ||
              response?.data?.session?.session_token;

            if (sessionTokenFromResponse) {
              tokenManager.setSessionToken(sessionTokenFromResponse);
            }
            if (response?.data?.session) {
              sessionManager.hydrateSessionMetadata(response.data.session, 'b2b_exchange');
            }

            const mappedUser = {
              ...validation.user,
              role: mappedRole,
              originalRole: backendRole // Keep original for debugging
            };

            // Cookie-only mode: Only store user data, not tokens
            // The backend has already set HttpOnly cookies during /auth/session/exchange
            tokenManager.setUserData(mappedUser);

            // Mark session as freshly verified so useAuthBootstrap doesn't
            // clear it if a concurrent /auth/me 401 arrives.
            sessionStorage.setItem('mira_auth_verified_at', String(Date.now()));

            // Clear magic link flag before broadcasting — the auth is complete
            sessionStorage.removeItem('mira_awaiting_magic_link');

            // Notify other tabs about the new login so they reload
            sessionManager.broadcast({ type: 'SESSION_CHANGED' });

            // Signal chat to start fresh instead of restoring old session
            sessionStorage.setItem('mira_fresh_login', 'true');

            // Set tenant-scoped session indicator for this successful login
            const orgSlug = getCurrentOrgSlug();
            localStorage.setItem(getTenantSessionIndicatorKey(orgSlug), 'true');

            // Dispatch Redux action to update authentication state
            if (!import.meta.env.PROD) {
              console.log('[OAuthCallback] dispatch loginSuccess/setUserRole', mappedRole);
            }
            dispatch(loginSuccess({
              user: mappedUser,
              authType: 'b2b'
            }));
            dispatch(setUserRole(mappedUser.role));

            result = {
              user: mappedUser,  // Use the mapped user with corrected role
              isFirstLogin: validation.metadata.isFirstLogin,
              needsOnboarding: validation.metadata.needsOnboarding,
              profileCompleted: validation.metadata.profileCompleted
            };
          } else {
            // Log the actual response for debugging
            if (!import.meta.env.PROD) {
              console.error('[OAuthCallback] No user data in response. Full response:', JSON.stringify(response.data));
            }
            throw new Error('No user data in response - authentication may have failed');
          }
        } else if (skipTokenExchange) {
          // Use existing authentication data
          result = {
            user: existingUser
          };
          // Set response data for redirect logic compatibility
          response = { data: { user: existingUser } };
        }

        // Successful authentication - redirect based on user role and context
        // Check if user has necessary profile data
        user = result?.user;  // This has the mapped role now

        console.log('[OAuthCallback] User for routing:', {
          role: user?.role,
          originalRole: user?.originalRole,
          fullUser: user
        });

        // Use flags from backend response for routing decisions
        const isFirstLogin = result?.isFirstLogin ?? response?.data?.is_first_login ?? false;
        let needsOnboarding = result?.needsOnboarding ?? response?.data?.needs_onboarding ?? false;
        const profileCompleted = result?.profileCompleted ?? response?.data?.profile_completed ?? false;

        if (!import.meta.env.PROD) {
          console.log('[OAuthCallback] Backend onboarding flags:', {
            'result?.isFirstLogin': result?.isFirstLogin,
            'response?.data?.is_first_login': response?.data?.is_first_login,
            'result?.needsOnboarding': result?.needsOnboarding,
            'response?.data?.needs_onboarding': response?.data?.needs_onboarding,
            'result?.profileCompleted': result?.profileCompleted,
            'response?.data?.profile_completed': response?.data?.profile_completed,
            'Final values': { isFirstLogin, needsOnboarding, profileCompleted }
          });
        }

        // Extra check: for consumers, redirect them to consumer dashboard
        // Consumers don't need the B2B onboarding flow - they have their own portal
        if (user?.role === 'consumer') {
          if (!import.meta.env.PROD) {
            console.log('[OAuthCallback] Consumer authentication - redirecting to consumer dashboard');
          }
          navigate(buildAppPath('/dashboard'), { replace: true });
          return;
        }

        // Clean up the processing flag after successful exchange
        if (processingKey) {
          sessionStorage.setItem(processingKey, 'processed');
        }

        if (!user) {
          // No user data, redirect to login
          navigate(buildPathForState(resolvedStateId, '/login/b2b?error=no_user_data'));
          return;
        }


        // Determine redirect path based on user role and authentication type
        let redirectPath;

        console.log('[OAuthCallback] Routing decision - user.role:', user.role);
        console.log('[OAuthCallback] Original role from backend:', user.originalRole);

        const requiresOnboarding = Boolean(needsOnboarding) || profileCompleted === false;

        // Check for platform admin roles (special case - not in landingPathForRole)
        if (user.role === 'superAdmin' || user.role === 'PLATFORM_SUPER_ADMIN' || user.role === 'PLATFORM_ADMIN') {
          redirectPath = '/admin';  // Platform admins go to /admin
        }
        // Check if employee needs onboarding using backend flags
        else if (requiresOnboarding) {
          if (!import.meta.env.PROD) {
            console.log('[OAuthCallback] Onboarding required. Routing to /onboard. Flags:', {
              isFirstLogin,
              needsOnboarding,
              profileCompleted,
              requiresOnboarding,
              userRole: user.role,
              originalRole: user.originalRole
            });
          }

          redirectPath = '/onboard';
        }
        // Use centralized role-based landing logic
        else {
          // Use the original backend role for proper routing
          redirectPath = landingPathForRole(user.originalRole || user.role);
          console.log('[OAuthCallback] Using landingPathForRole - routing to:', redirectPath);
        }
        
        // Check if we have organization/branch context from the magic link or host
        const orgSlugFromQuery = searchParams.get('org') || searchParams.get('slug');
        const slugFromUser = user?.organization_slug || user?.organizationSlug || user?.organization?.slug || user?.organization?.subdomain;
        const slugFromResponse = response?.data?.user?.organization_slug || response?.data?.organization_subdomain;
        const slugFromContext = orgContext?.slug || orgContext?.organizationSlug;
        const orgSlugCandidates = [
          slugFromUser,
          slugFromResponse,
          slugFromContext,
          getCurrentOrgSlug(),
          orgSlugFromQuery
        ];
        const resolvedOrgSlug = orgSlugCandidates.find(slug => slug && validateOrgSlug(slug)) || null;

        const branchFromQuery = searchParams.get('branch') || searchParams.get('branch_slug');
        const branchFromUser = user?.branch_slug || user?.branchSlug;
        const branchFromResponse = response?.data?.branch_slug || response?.data?.branchSlug;
        const branchFromContext = orgContext?.branchSlug;
        const branchFromHostname = getCurrentBranchSlug();
        const branchCandidates = [
          branchFromUser,
          branchFromResponse,
          branchFromContext,
          branchFromQuery,
          branchFromHostname
        ];
        let resolvedBranchSlug =
          branchCandidates.find(slug => slug && slug !== '*' && validateOrgSlug(slug)) || null;
        if (!resolvedBranchSlug && branchCandidates.includes('*')) {
          resolvedBranchSlug = '*';
        }

        if (resolvedOrgSlug) {
          rememberTenantContext({
            orgSlug: resolvedOrgSlug,
            clientSlug: slugFromUser || slugFromResponse || resolvedOrgSlug,
            branchSlug: resolvedBranchSlug ?? undefined
          });
        }

        if (!import.meta.env.PROD) {
          console.log('[OAuthCallback] Resolved tenant context', {
            resolvedOrgSlug,
            orgSources: {
              slugFromUser,
              slugFromResponse,
              slugFromContext,
              currentOrgSlug: getCurrentOrgSlug(),
              orgSlugFromQuery
            },
            resolvedBranchSlug,
            branchSources: { branchFromQuery, branchFromUser, branchFromResponse, branchFromContext }
          });
        }

        // Determine if we need cross-subdomain redirect or can use local navigation
        const baseDomain = import.meta.env.VITE_APP_BASE_DOMAIN || 'localhost';
        const currentHostname = window.location.hostname;

        const targetHostname = (() => {
          if (!resolvedOrgSlug) return baseDomain;
          // Branch + org joined with hyphen as single label (matches backend format)
          if (resolvedBranchSlug && resolvedBranchSlug !== '*') {
            return `${resolvedBranchSlug}-${resolvedOrgSlug}.${baseDomain}`;
          }
          return `${resolvedOrgSlug}.${baseDomain}`;
        })();

        const isAlreadyOnTargetSubdomain = (() => {
          if (!resolvedOrgSlug) {
            return currentHostname === baseDomain;
          }
          if (resolvedBranchSlug) {
            // Branch + org joined with hyphen as single label (matches backend format)
            const branchHost = `${resolvedBranchSlug}-${resolvedOrgSlug}`;
            return currentHostname === `${branchHost}.${baseDomain}` || currentHostname.startsWith(`${branchHost}.`);
          }
          return currentHostname === `${resolvedOrgSlug}.${baseDomain}` || currentHostname.startsWith(`${resolvedOrgSlug}.`);
        })();

        if (!import.meta.env.PROD) {
          console.log('[OAuthCallback] Final redirect decision:', {
            userRole: user.role,
            redirectPath,
            orgSlug: resolvedOrgSlug,
            branchSlug: resolvedBranchSlug,
            currentHostname,
            targetHostname,
            isAlreadyOnTargetSubdomain
          });
        }

        const stateAwareRedirectPath = buildPathForState(resolvedStateId, redirectPath);

        if (resolvedOrgSlug && !isAlreadyOnTargetSubdomain) {
          // Cross-subdomain redirect needed - NO tokens in URL
          const subdomainUrl = buildTenantUrl({
            clientSlug: resolvedOrgSlug,
            branchSlug: resolvedBranchSlug || getCurrentBranchSlug(),
            path: stateAwareRedirectPath,
          });
          console.log('[OAuthCallback] Cross-subdomain redirect to:', subdomainUrl);
          processingTokenRef.current = authToken;  // Mark this token as processed
          window.location.href = subdomainUrl;
        } else {
          // Already on correct subdomain or no organization context - use local navigation
          console.log('[OAuthCallback] Local navigation to:', stateAwareRedirectPath);
          processingTokenRef.current = authToken;  // Mark this token as processed
          navigate(stateAwareRedirectPath, { replace: true });  // Use replace for RBAC redirects
        }
      } catch (err) {
        if (!import.meta.env.PROD) {
          console.error('[OAuthCallback] Authentication callback processing failed:', err.message, err.response?.status);
        }

        processingTokenRef.current = authToken;  // Mark as processed even on error

        // Clean up the processing flag on error as well
        if (processingKey) {
          sessionStorage.setItem(processingKey, 'processed');
        }

        // Use utility function for consistent error messages
        const errorMessage = getAuthErrorMessage(err);

        // Determine if we should redirect to login without error or with error
        const shouldRetryLogin = err.message === 'TOKEN_ALREADY_CONSUMED' ||
                                err.response?.status === 401;

        // Store error details for support
        sessionStorage.setItem('auth_error', JSON.stringify({
          timestamp: new Date().toISOString(),
          error: errorMessage,
          status: err.response?.status,
          details: err.response?.data
        }));

        // Redirect with appropriate error message
        if (shouldRetryLogin) {
          navigate(buildPathForState(resolvedStateId, loginBasePath));
        } else {
          navigate(buildPathForState(resolvedStateId, `${loginBasePath}?error=${encodeURIComponent(errorMessage)}`));
        }
      }
    };

    processCallback();

    // Cleanup function to remove the processing flag when component unmounts
    return () => {
      if (processingKeyRef.current) {
        sessionStorage.setItem(processingKeyRef.current, 'processed');
      }
    };
  }, [dispatch, navigate, searchParams]); // Include necessary dependencies

  if (phase === 'badge') {
    return <B2CLoginBadge userName={badgeUserName} />;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <CardLoader />
        <p className="mt-4 text-gray-600">Processing authentication...</p>
      </div>
    </div>
  );
};

export default OAuthCallback;
